import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { compose, withHandlers, withProps, withPropsOnChange, withState } from 'recompose';
import config from '../../../../config';
import { getShop } from '../../../auth/selectors';
import withTimezone from '../../../auth/utils/withTimezone';
import confirmModal from '../../../common/confirmModal';
import withToggle from '../../../common/hoc/withToggle';
import withNotifications from '../../../common/withNotifications';
import { removeTimeEntry, updateTimeEntry } from '../actions';
import EditTimeTrackingEntry from '../components/EditTimeTrackingEntry';
import withExtendedTimeTracking from '../hoc/withExtendedTimeTracking';
import withTimeTicker from '../hoc/withTimeTicker';
import { formatDuration, getFormattedDuration } from '../selectors';

const getTimes = (props) => {
  const day = moment.tz(props.currentDate, config.dateFormatISO, props.timezone);
  const startedAt = moment(day).hours(props.startedAtHours).minutes(props.startedAtMinutes).toJSON();
  const endedAt = props.entry.active
    ? new Date()
    : moment(day).hours(props.endedAtHours).minutes(props.endedAtMinutes).toJSON();
  return {
    startedAt,
    endedAt,
  };
};

const EditTimeTrackingEntryContainer = compose(
  withNotifications,
  withTimezone,
  withExtendedTimeTracking,
  connect(
    state => ({
      shopId: getShop(state),
    }),
    { updateTimeEntry, removeTimeEntry },
  ),
  withToggle('passcodeModal'),
  withState('duration', 'setDuration', props => getFormattedDuration(props.entry)),
  withState('startedAtHours', 'setStartedAtHours', props => moment.tz(props.entry.startedAt, props.timezone).hours()),
  withState(
    'startedAtMinutes',
    'setStartedAtMinutes',
    props => moment.tz(props.entry.startedAt, props.timezone).minutes()
  ),
  withState('endedAtHours', 'setEndedAtHours', props => moment.tz(props.entry.endedAt, props.timezone).hours()),
  withState('endedAtMinutes', 'setEndedAtMinutes', props => moment.tz(props.entry.endedAt, props.timezone).minutes()),
  withState('role', 'setRole', props => props.entry.role),
  withState('passcode', 'setPasscode', ''),
  withState('pristine', 'setPristine', true),
  withState('valid', 'setValid', true),
  withState('protectedCallback', 'setProtectedCallback'),
  withTimeTicker(props => props.entry.active),
  withHandlers({
    resetValues: props => () => {
      props.setStartedAtHours(moment.tz(props.entry.startedAt, props.timezone).hours());
      props.setStartedAtMinutes(moment.tz(props.entry.startedAt, props.timezone).minutes());
      props.setEndedAtHours(moment.tz(props.entry.endedAt, props.timezone).hours());
      props.setEndedAtMinutes(moment.tz(props.entry.endedAt, props.timezone).minutes());
      props.setRole(props.entry.role);
      props.setPristine(true);
    },
  }),
  withHandlers({
    onPasscodeModalClose: props => () => {
      props.setPasscode('');
      props.setProtectedCallback(undefined);
      props.passcodeModalOff();
    },
    onPasscodeChange: props => (e) => {
      props.setPasscode(e.target.value);
    },
    onStartedAtHoursChange: props => (e) => {
      props.setStartedAtHours(e.target.value || 0);
      props.setPristine(false);
    },
    onStartedAtMinutesChange: props => (e) => {
      props.setStartedAtMinutes(e.target.value || 0);
      props.setPristine(false);
    },
    onEndedAtHoursChange: props => (e) => {
      props.setEndedAtHours(e.target.value || 0);
      props.setPristine(false);
    },
    onEndedAtMinutesChange: props => (e) => {
      props.setEndedAtMinutes(e.target.value || 0);
      props.setPristine(false);
    },
    onRoleChange: props => (e) => {
      props.setRole(e.target.value);
      props.setPristine(false);
    },
    saveChanges: props => () => {
      const { startedAt, endedAt } = getTimes(props);

      const payload = {
        startedAt,
        endedAt,
        shopId: props.shopId,
        userId: props.entry.userId,
      };

      if (props.extendedHourlyWages) {
        payload.role = props.role;
      }

      if (props.requirePasscode) {
        payload.passcode = props.passcode;
      }

      const handleSuccess = () => {
        props.addNotification({
          title: 'Success',
          level: 'success',
          message: 'Entry successfully updated.',
        });
        props.setPristine(true);
      };

      const handleError = (error) => {
        if (error.status === 422) {
          props.resetValues();
        }
      };

      props.updateTimeEntry(props.entry.barberId, props.entry.id, payload)
        .then(handleSuccess, handleError);
    },
    deleteEntry: props => async () => {
      const confirmed = await confirmModal({
        type: 'danger',
        title: 'Delete time entry',
        message: 'Are you sure you want to remove this time entry?',
      });

      if (!confirmed) {
        return false;
      }

      const { barberId, ...payload } = props.entry;

      payload.shopId = props.shopId;

      if (props.requirePasscode) {
        payload.passcode = props.passcode;
      }

      return props.removeTimeEntry(barberId, payload)
        .then(() =>
          props.addNotification({
            title: 'Success',
            level: 'success',
            message: 'Entry successfully removed.',
          })
        );
    },
  }),
  withHandlers({
    executeCallback: props => (callbackName) => {
      switch (callbackName) {
        case 'deleteEntry':
          return props.deleteEntry();
        case 'saveChanges':
          return props.saveChanges();
        default:
          return undefined;
      }
    },
  }),
  withHandlers({
    passcodeProtected: props => callbackName => () => {
      if (props.requirePasscode) {
        props.setProtectedCallback(callbackName);
        props.passcodeModalOn();

        return;
      }

      props.executeCallback(callbackName);
    },
    confirmPasscode: props => () => {
      props.executeCallback(props.protectedCallback);
      props.onPasscodeModalClose();
    },
  }),
  withPropsOnChange(
    ['startedAtHours', 'startedAtMinutes', 'endedAtHours', 'endedAtMinutes', 'role'],
    (props) => {
      if (props.pristine) return;
      const { startedAt, endedAt } = getTimes(props);
      const isValid =
        props.startedAtHours <= 23 && props.startedAtMinutes <= 59 &&
        (props.entry.active || (props.endedAtHours <= 23 && props.endedAtMinutes <= 59)) &&
        moment(startedAt).isBefore(endedAt);
      props.setValid(isValid);
      props.setDuration(isValid ? getFormattedDuration({ startedAt, endedAt }) : '-');
    }
  ),
  /**
   * TODO: previous props generator does the same thing, but without "clockedMinutes". should be joined
   */
  withProps((props) => {
    if (!props.valid) {
      return {};
    }

    const [hours, minutes] = props.duration.split(' ');
    const duration = moment
      .duration({
        hours: Number(hours.split('h')[0]),
        minutes: Number(minutes.split('m')[0]),
      })
      .add(props.clockedMinutes, 'm');

    return {
      duration: formatDuration(duration),
    };
  }),
)(EditTimeTrackingEntry);

export default EditTimeTrackingEntryContainer;
